import { backoffDelay, DEFAULT_BACKOFF, type BackoffOptions } from '../lib/backoff.js';
import { liveValue, type LiveValue } from './types.js';

/** The part of the browser WebSocket the live connection uses. */
export interface SocketLike {
  onopen: ((ev: Event) => void) | null;
  onmessage: ((ev: MessageEvent) => void) | null;
  onclose: ((ev: CloseEvent) => void) | null;
  onerror: ((ev: Event) => void) | null;
  close(code?: number, reason?: string): void;
}

export type SocketFactory = (url: string) => SocketLike;

let defaultFactory: SocketFactory = (url) => new WebSocket(url);

/** Replace how sockets are opened (the dev mock installs an in-page fake). */
export function setSocketFactory(factory: SocketFactory): void {
  defaultFactory = factory;
}

export type LiveStatus = 'idle' | 'connecting' | 'open' | 'reconnecting';

export interface LiveConnectionOptions {
  url: (pointIds: readonly string[]) => string;
  onValues: (values: LiveValue[]) => void;
  onStatus?: (status: LiveStatus) => void;
  createSocket?: SocketFactory;
  backoff?: BackoffOptions;
  random?: () => number;
  /** Updates are delivered in batches at most this often (ms); 0 = immediately. */
  batchMs?: number;
}

/**
 * One WebSocket to `/api/v1/live` for a set of points. Reconnects with
 * exponential backoff when it drops and reopens when the point set changes.
 */
export class LiveConnection {
  private readonly opts: LiveConnectionOptions;
  private socket: SocketLike | null = null;
  private points: string[] = [];
  private key = '';
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private batchTimer: ReturnType<typeof setTimeout> | null = null;
  private pending: LiveValue[] = [];
  private closed = false;

  constructor(options: LiveConnectionOptions) {
    this.opts = options;
  }

  /** Subscribe to exactly these points; a no-op if the set is unchanged. */
  setPoints(ids: readonly string[]): void {
    if (this.closed) return;
    const unique = [...new Set(ids)].sort();
    const key = unique.join(',');
    if (key === this.key) return;
    this.key = key;
    this.points = unique;
    this.attempt = 0;
    this.reopen();
  }

  close(): void {
    this.closed = true;
    this.clearRetry();
    if (this.batchTimer) clearTimeout(this.batchTimer);
    this.batchTimer = null;
    this.pending = [];
    this.dropSocket();
    this.opts.onStatus?.('idle');
  }

  private reopen(): void {
    this.clearRetry();
    this.dropSocket();
    if (this.points.length === 0) {
      this.opts.onStatus?.('idle');
      return;
    }
    this.connect();
  }

  private connect(): void {
    this.opts.onStatus?.(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const create = this.opts.createSocket ?? defaultFactory;
    let socket: SocketLike;
    try {
      socket = create(this.opts.url(this.points));
    } catch {
      this.scheduleRetry();
      return;
    }
    this.socket = socket;
    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.attempt = 0;
      this.opts.onStatus?.('open');
    };
    socket.onmessage = (ev) => {
      if (this.socket !== socket) return;
      this.receive(ev.data);
    };
    socket.onerror = () => {
      // A close event follows; reconnect happens there.
    };
    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.scheduleRetry();
    };
  }

  private receive(data: unknown): void {
    if (typeof data !== 'string') return;
    let json: unknown;
    try {
      json = JSON.parse(data);
    } catch {
      return;
    }
    const items = Array.isArray(json) ? (json as unknown[]) : [json];
    for (const item of items) {
      const parsed = liveValue.safeParse(item);
      if (parsed.success) this.pending.push(parsed.data);
    }
    this.flushSoon();
  }

  private flushSoon(): void {
    const batchMs = this.opts.batchMs ?? 0;
    if (batchMs <= 0) {
      this.flush();
      return;
    }
    this.batchTimer ??= setTimeout(() => {
      this.batchTimer = null;
      this.flush();
    }, batchMs);
  }

  private flush(): void {
    if (this.pending.length === 0) return;
    const values = this.pending;
    this.pending = [];
    this.opts.onValues(values);
  }

  private scheduleRetry(): void {
    if (this.closed || this.points.length === 0) return;
    const delay = backoffDelay(
      this.attempt,
      this.opts.backoff ?? DEFAULT_BACKOFF,
      this.opts.random,
    );
    this.attempt++;
    this.opts.onStatus?.('reconnecting');
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private dropSocket(): void {
    const s = this.socket;
    this.socket = null;
    if (!s) return;
    s.onopen = null;
    s.onmessage = null;
    s.onclose = null;
    s.onerror = null;
    try {
      s.close(1000, 'unsubscribe');
    } catch {
      // already closed
    }
  }
}
